type ThemeId = 'luscious' | 'brutalist' | 'playful' | 'zen' | 'trippy';
type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeSession {
  theme: ThemeId;
  assignedAt: number;
}

interface ThemePreferences {
  mode: ThemeMode;
  legacyCyclingEnabled: boolean;
}

const SESSION_KEY = 'pursuitdeck_theme_session';
const PREFERENCES_KEY = 'pursuitdeck_theme_preferences';
const THEMES: ThemeId[] = ['brutalist', 'playful', 'zen', 'trippy'];
const DEFAULT_THEME: ThemeId = 'luscious';
const SESSION_DURATION_MS = 5 * 60 * 1000;

const readJson = <T,>(key: string): T | null => {
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: unknown): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A storage failure only limits cross-page theme continuity.
  }
};

const isTheme = (value: unknown): value is ThemeId =>
  value === DEFAULT_THEME || THEMES.includes(value as ThemeId);

const resolveMode = (mode: ThemeMode): 'light' | 'dark' => {
  if (mode === 'light' || mode === 'dark') return mode;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const selectTheme = (): ThemeId => {
  const preferences = readJson<ThemePreferences>(PREFERENCES_KEY);
  if (!preferences?.legacyCyclingEnabled) return DEFAULT_THEME;

  const session = readJson<ThemeSession>(SESSION_KEY);
  if (
    session &&
    isTheme(session.theme) &&
    session.theme !== DEFAULT_THEME &&
    Date.now() - session.assignedAt < SESSION_DURATION_MS
  ) {
    return session.theme;
  }

  const next = THEMES[Math.floor(Math.random() * THEMES.length)] ?? 'zen';
  writeJson(SESSION_KEY, {theme: next, assignedAt: Date.now()});
  return next;
};

export const applyTheme = (): (() => void) => {
  const root = document.documentElement;

  const render = () => {
    const preferences = readJson<ThemePreferences>(PREFERENCES_KEY) ?? {
      mode: 'system',
      legacyCyclingEnabled: false,
    };
    root.dataset.theme = selectTheme();
    root.dataset.colorMode = resolveMode(preferences.mode);
  };

  render();
  const interval = window.setInterval(render, 30_000);
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', render);
  window.addEventListener('storage', render);

  return () => {
    window.clearInterval(interval);
    media.removeEventListener('change', render);
    window.removeEventListener('storage', render);
  };
};
